import { stillBlocked } from './blocked';
import type { Issue } from './types';
import { DEFAULT_FILTERS, FILTER_NOBODY, type Filters } from './filters';
import { FILTER_NO_AREA } from './areas-report';
import { FILTER_NO_PHASE } from './phases';

/**
 * SAVED VIEWS, AND THE FOUR THAT COME WITH THE BOARD — #301.
 *
 * Vinnie, 30 August 2026: *"we can have saved views, I think that would be
 * useful to users as it gives them the flexibility to customize the way they
 * work."* And, on the four in the mockup: *"I like the mockup with the taps on
 * the top showing open, my work, blocked."*
 *
 * ── WHY A VIEW IS NOT JUST A SAVED FILTER SET ───────────────────────────────
 *
 * The five status chips carried counts, and the counts were the most-read thing
 * in the whole bar. Moving to a Filter menu would have lost them — a menu is
 * shut most of the time. So the counts move onto the views, which are always on
 * screen, and a view is therefore a filter set THAT KNOWS ITS OWN SIZE.
 *
 * ── AND WHY THE BUILT-IN FOUR ARE FUNCTIONS RATHER THAN FILTER SETS ─────────
 *
 * "My work" is *assigned to me*, and there is no `me` in a saved filter set —
 * storing an address would make one person's view useless to everybody else and
 * wrong the moment they changed their email. "Blocked" reads a field the filter
 * model has no control for at all. Both are questions rather than selections,
 * so they are written as questions. A view somebody SAVES is a filter set,
 * because that is exactly what they were doing when they saved it.
 */

export type BuiltIn = 'open' | 'mine' | 'blocked' | 'everything';

export interface View {
  id: string;
  name: string;
  /** Built-in views answer a question; saved ones carry a filter set. `all` is
   *  the pool being filtered — the Blocked view needs it to ask stillBlocked
   *  (whether a named blocker card is finished); the others ignore it. */
  ask?: (i: Issue, me: string, all?: Issue[]) => boolean;
  filters?: Filters;
  /** Which board it was saved on. Built-ins belong to all of them. */
  board?: string;
}

/**
 * DONE IS HIDDEN BY THE VIEW, not by a filter default.
 *
 * It used to be `DEFAULT_FILTERS.hiddenStatuses = ['done']` — true, and
 * invisible: a greyed-out chip implying it rather than a word saying it. The
 * mockup puts it in the count line, *"Done is hidden by this view"*, which is
 * the same fact where somebody will read it.
 */
export const BUILT_IN: View[] = [
  {
    id: 'open',
    name: 'Open',
    /*
     * `wontdo` STAYS OUT OF "OPEN" AND IN "EVERYTHING". Work that was tried and
     * ruled out is not open — but the filter default deliberately keeps it
     * visible, because "knowing what was ruled out is exactly what stops it
     * being suggested again". Open is about what is left to do; nothing is lost,
     * it is one view away.
     */
    ask: (i) => i.status !== 'done' && i.status !== 'wontdo',
  },
  {
    /*
     * ASSIGNED TO ME AND NOT FINISHED. "My work" showing forty things somebody
     * closed last month is a list of what they HAVE done, which is a different
     * screen and not one anybody opens a backlog for.
     */
    id: 'mine',
    name: 'My Work',
    ask: (i, me) => Boolean(me) && i.assignee === me && i.status !== 'done' && i.status !== 'wontdo',
  },
  {
    /* The FIELD, not the status. A card can be in progress and blocked, and it
       is the blocked one you want to see.
       THROUGH `stillBlocked`, never the raw field: somebody typing "No" or
       "n/a" into "what is blocking this" means it is not blocked (the fault #299
       was raised for), AND a blocker whose named card is finished has cleared, so
       a dependent whose predecessor is Done — and a finished card — must drop out
       of the Blocked view the way they already drop off the card face and the
       metrics (L160). Needs the pool to read each blocker card's status; the
       crafted single-card probe in `hidesDone` passes `[i]`. */
    id: 'blocked',
    name: 'Blocked',
    ask: (i, _me, all) => stillBlocked(i, all ?? [i]),
  },
  {
    /*
     * EVERYTHING MEANS EVERYTHING, including done and abandoned. It is the
     * escape hatch from every other view, so it cannot itself hide anything —
     * a "show me all of it" that quietly holds 75% back is the fault this card
     * exists to fix, wearing the one label nobody would check.
     */
    id: 'everything',
    name: 'Everything',
    ask: () => true,
  },
];

/**
 * A VIEW SOMEBODY SAVED, as it is stored against their account.
 *
 * The filter set as it stood when they pressed Save, and the board it was saved
 * on — because a view naming an area or an epic means nothing on a board that
 * has neither, and offering it there would be a tab that always shows zero.
 */
export interface SavedView {
  id: string;
  name: string;
  board: string;
  filters: Filters;
}

/** The four that come with the board, then this person's, for this board. */
export function viewsFor(saved: SavedView[], board: string): View[] {
  return [...BUILT_IN, ...saved.filter((v) => v.board === board).map((v) => ({ ...v }))];
}

export const DEFAULT_VIEW = 'open';

/**
 * Everything a view leaves in, before any filter is applied.
 *
 * ⚠️ A SAVED VIEW COUNTS BY ITS OWN FILTERS, and that matters for the number on
 * its tab. Left as "all issues", a saved view reading "My area" would sit in the
 * row claiming 300 beside an Open claiming 78 — the one number on that screen
 * nobody would think to doubt, because a tab count is not something you check.
 */
export function inView(
  issues: Issue[],
  view: View | undefined,
  me: string,
  noEpic?: string
): Issue[] {
  if (!view) return issues;
  if (view.ask) return issues.filter((i) => view.ask!(i, me, issues));
  if (view.filters) return issues.filter((i) => passes(i, view.filters!, noEpic));
  return issues;
}

/**
 * True when this view hides finished work, so the count line can say so.
 *
 * ⚠️ ASKED AS A COMPARISON, not by handing the view a bare `{ status: 'done' }`.
 * That is what this did first, and Blocked answered "yes, I hide finished work"
 * — because it turned the stub down for having no blocker, which has nothing to
 * do with Done. The backlog would then have printed *"Done is hidden by this
 * view"* under a view that hides no such thing, which is a smaller version of
 * exactly the lie this card was raised to remove.
 *
 * So: take one card the view accepts, finish it, and see whether it drops out.
 */
export const hidesDone = (view: View | undefined): boolean => {
  if (!view?.ask) return false;
  const me = 'someone@example.com';
  /* Everything a built-in view looks at, all satisfied at once, so the only
     thing that can change the answer below is the status. */
  const open = { status: 'todo', assignee: me, blocked: 'waiting' } as Issue;
  const doneTwin = { ...open, status: 'done' } as Issue;
  // Pass each probe card as its own pool so the Blocked view's stillBlocked sees
  // the status it is probing (a done card is never blocked).
  return view.ask(open, me, [open]) && !view.ask(doneTwin, me, [doneTwin]);
};

/**
 * Does one card pass one filter set?
 *
 * ⚠️ EVERY FIELD READS "EMPTY MEANS EVERY VALUE", and that is the rule the whole
 * screen rests on. Read the other way — a missing key matching nothing — one
 * unset filter empties the backlog and there is nothing on screen to say why,
 * which is the defect #301 was raised about arriving from inside the fix.
 */
export function passes(i: Issue, f: Filters, noEpic = '(No epic)'): boolean {
  const any = (on: string[], has: (v: string) => boolean) => on.length === 0 || on.some(has);
  return (
    any(f.status, (v) => i.status === v) &&
    /* WITHIN a field the values are OR — "UI or Workflow" is what the pill
       says. ACROSS fields they are AND, because each one narrows. */
    any(f.area, (v) => (v === FILTER_NO_AREA ? !i.area : (i.area ?? '') === v)) &&
    /* Phase reads exactly as area does — a real "no phase" answer, never a gap,
       because most cards have none and that is the set people go looking for. */
    any(f.phase, (v) => (v === FILTER_NO_PHASE ? !i.phase : (i.phase ?? '') === v)) &&
    any(f.epic, (v) => (i.epic || noEpic) === v) &&
    any(f.size, (v) => i.size === v) &&
    any(f.priority, (v) => i.priority === v) &&
    any(f.type, (v) => i.kind === v) &&
    any(f.assignee, (v) => (v === FILTER_NOBODY ? !i.assignee : i.assignee === v)) &&
    any(f.responsible, (v) => (v === FILTER_NOBODY ? !i.responsible : i.responsible === v))
  );
}

/** Which fields are switched on, for the pills and for "is anything on". */
export const FIELDS = ['status', 'area', 'phase', 'epic', 'size', 'priority', 'type', 'assignee', 'responsible'] as const;
export type Field = (typeof FIELDS)[number];

export const FIELD_LABEL: Record<Field, string> = {
  status: 'Status',
  area: 'Area',
  phase: 'Phase',
  epic: 'Epic',
  size: 'Size',
  priority: 'Priority',
  type: 'Type',
  assignee: 'Accountable',
  responsible: 'Responsible',
};

/** Clearing the filters keeps the sort, which hides nothing and was not asked about. */
export const cleared = (f: Filters): Filters => ({ ...DEFAULT_FILTERS, sort: f.sort });
